import { useState } from "react";
import { Card, CardContent,  CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trophy, Medal, Award, Eye, EyeOff, Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface StudentRanking {
  id: string;
  name: string;
  class: string;
  overallScore: number;
  rank: number;
  percentile: number;
  subjects: Record<string, number>;
}

const RankingSystem = () => {
  const { toast } = useToast();
  const [showRankings, setShowRankings] = useState(true);
  const [rankingType, setRankingType] = useState("overall");
  const [selectedClass, setSelectedClass] = useState("all");
  const [settings, setSettings] = useState({
    showToStudents: true,
    includeInReports: true,
    autoUpdate: true
  });

  const studentRankings: StudentRanking[] = [
    {
      id: "1",
      name: "Alice Johnson",
      class: "Grade 12A",
      overallScore: 95.2,
      rank: 1,
      percentile: 99,
      subjects: { math: 98, english: 94, science: 96, history: 92 }
    },
    {
      id: "2",
      name: "Bob Smith",
      class: "Grade 12A",
      overallScore: 92.8,
      rank: 2,
      percentile: 95,
      subjects: { math: 95, english: 91, science: 94, history: 91 }
    },
    {
      id: "3",
      name: "Carol Williams",
      class: "Grade 12B",
      overallScore: 90.5,
      rank: 3,
      percentile: 90,
      subjects: { math: 89, english: 93, science: 91, history: 89 }
    },
    {
      id: "4",
      name: "David Brown",
      class: "Grade 12A",
      overallScore: 88.7,
      rank: 4,
      percentile: 85,
      subjects: { math: 87, english: 90, science: 89, history: 88 }
    },
    {
      id: "5",
      name: "Emma Davis",
      class: "Grade 12B",
      overallScore: 87.3,
      rank: 5,
      percentile: 80,
      subjects: { math: 85, english: 89, science: 88, history: 87 }
    }
  ];

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="w-4 h-4 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-4 h-4 text-gray-400" />;
    if (rank === 3) return <Award className="w-4 h-4 text-amber-600" />;
    return null;
  };

  const getPercentileBadge = (percentile: number) => {
    if (percentile >= 95) return <Badge className="bg-green-500 text-xs">Top 5%</Badge>;
    if (percentile >= 75) return <Badge variant="secondary" className="text-xs">Top 25%</Badge>;
    if (percentile >= 50) return <Badge variant="outline" className="text-xs">Top 50%</Badge>;
    return <Badge variant="destructive" className="text-xs">Bottom 50%</Badge>;
  };

  const handleFilterChange = (type: string, value: string) => {
    if (type === "ranking") {
      setRankingType(value);
    } else if (type === "class") {
      setSelectedClass(value);
    }
    
    toast({
      title: "Filter Applied",
      description: `Rankings filtered by ${type}: ${value}`
    });
  };

  const handleExport = () => {
    toast({
      title: "Export Started",
      description: "Rankings are being exported..."
    });
  };

  const handleSettingChange = (setting: string, value: boolean) => {
    setSettings(prev => ({ ...prev, [setting]: value }));
    toast({
      title: "Setting Updated",
      description: `${setting} ${value ? 'enabled' : 'disabled'}`
    });
  };

  return (
    <div className="space-y-2 m-2">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 md:gap-4 m-2">
        <div>
          <h3 className="text-xl md:text-2xl font-semibold">Student Rankings</h3>
          <p className="text-sm text-muted-foreground">Automated positioning and performance ranking</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
          <div className="flex items-center gap-2">
            <Switch 
              checked={showRankings} 
              onCheckedChange={(value) => {
                setShowRankings(value);
                toast({
                  title: value ? "Rankings Shown" : "Rankings Hidden",
                  description: `Rankings are now ${value ? 'visible' : 'hidden'}`
                });
              }}
              id="show-rankings"
            />
            <Label htmlFor="show-rankings" className="text-sm">
              {showRankings ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </Label>
          </div>
          
          <Select value={rankingType} onValueChange={(value) => handleFilterChange("ranking", value)}>
            <SelectTrigger className="w-full sm:w-32 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] dark:[&_[data-state=hovered]]:bg-[#1E293B]">
              <SelectItem value="overall" className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">Overall</SelectItem>
              <SelectItem value="class" className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">By Class</SelectItem>
              <SelectItem value="subject" className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">By Subject</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={selectedClass} onValueChange={(value) => handleFilterChange("class", value)}>
            <SelectTrigger className="w-full sm:w-32 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] dark:[&_[data-state=hovered]]:bg-[#1E293B]">
              <SelectItem value="all" className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">All Classes</SelectItem>
              <SelectItem value="12a" className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">Grade 12A</SelectItem>
              <SelectItem value="12b" className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">Grade 12B</SelectItem>
              <SelectItem value="11a" className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">Grade 11A</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={handleExport} className="w-full sm:w-auto border border-[#E2E8F0] dark:border-[#1E293B]">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Top Performers Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
        {studentRankings.slice(0, 3).map((student, index) => (
          <Card key={student.id} className={`border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] m-2 ${index === 0 ? 'ring-2 ring-yellow-500' : ''}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getRankIcon(student.rank)}
                  <span className="font-bold text-lg">#{student.rank}</span>
                </div>
                {getPercentileBadge(student.percentile)}
              </div>
            </CardHeader>
            <CardContent>
              <h3 className="font-semibold text-sm sm:text-base">{student.name}</h3>
              <p className="text-xs sm:text-sm text-muted-foreground mb-2">{student.class}</p>
              <div className="text-center">
                <p className="text-xl sm:text-2xl font-bold text-primary">{student.overallScore}%</p>
                <p className="text-xs text-muted-foreground">Overall Score</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Full Rankings Table */}
      {showRankings && (
        <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] m-2">
          <CardHeader>
            <CardTitle className="text-sm sm:text-base">Complete Rankings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs sm:text-sm">Rank</TableHead>
                    <TableHead className="text-xs sm:text-sm">Student</TableHead>
                    <TableHead className="text-xs sm:text-sm">Class</TableHead>
                    <TableHead className="text-xs sm:text-sm">Overall Score</TableHead>
                    <TableHead className="text-xs sm:text-sm">Percentile</TableHead>
                    <TableHead className="text-xs sm:text-sm">Math</TableHead>
                    <TableHead className="text-xs sm:text-sm">English</TableHead>
                    <TableHead className="text-xs sm:text-sm">Science</TableHead>
                    <TableHead className="text-xs sm:text-sm">History</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {studentRankings.map((student) => (
                    <TableRow key={student.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {getRankIcon(student.rank)}
                          #{student.rank}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm">{student.name}</TableCell>
                      <TableCell className="text-xs sm:text-sm">{student.class}</TableCell>
                      <TableCell className="text-xs sm:text-sm font-semibold">{student.overallScore}%</TableCell>
                      <TableCell>
                        {getPercentileBadge(student.percentile)}
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm">{student.subjects.math}%</TableCell>
                      <TableCell className="text-xs sm:text-sm">{student.subjects.english}%</TableCell>
                      <TableCell className="text-xs sm:text-sm">{student.subjects.science}%</TableCell>
                      <TableCell className="text-xs sm:text-sm">{student.subjects.history}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ranking Configuration */}
      <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] m-2">
        <CardHeader>
          <CardTitle className="text-sm sm:text-base">Ranking Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            <div className="flex items-center justify-between p-3 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg bg-[#FFFFFF] dark:bg-[#020817]">
              <Label className="text-sm">Show Rankings to Students</Label>
              <Switch 
                checked={settings.showToStudents}
                onCheckedChange={(value) => handleSettingChange("showToStudents", value)}
              />
            </div>
            <div className="flex items-center justify-between p-3 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg bg-[#FFFFFF] dark:bg-[#020817]">
              <Label className="text-sm">Include in Report Cards</Label>
              <Switch 
                checked={settings.includeInReports}
                onCheckedChange={(value) => handleSettingChange("includeInReports", value)}
              />
            </div>
            <div className="flex items-center justify-between p-3 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg bg-[#FFFFFF] dark:bg-[#020817]">
              <Label className="text-sm">Auto-update Rankings</Label>
              <Switch 
                checked={settings.autoUpdate}
                onCheckedChange={(value) => handleSettingChange("autoUpdate", value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RankingSystem;